<template>
  <transition name="drawer-slide">
    <div v-if="visible" class="ticket-drawer-wrapper">
      <div class="ticket-drawer-overlay" @click="$emit('close')" />
      <aside class="ticket-drawer" role="dialog" aria-modal="true" aria-label="Ticket details">
        <div class="ticket-drawer__header">
          <span class="ticket-drawer__number">#{{ ticket.ticketNbr }}</span>
          <button class="ticket-drawer__close" type="button" aria-label="Close" @click="$emit('close')">
            <b-icon icon="close" size="is-small" />
          </button>
        </div>

        <div class="ticket-drawer__body">
          <p v-if="ticket.summary" class="ticket-drawer__summary">{{ ticket.summary }}</p>

          <dl class="ticket-drawer__fields">
            <div v-if="clientName" class="ticket-drawer__field">
              <dt>Client</dt>
              <dd>{{ clientName }}</dd>
            </div>
            <div v-if="technicianDisplay" class="ticket-drawer__field">
              <dt>Technician</dt>
              <dd>{{ technicianDisplay }}</dd>
            </div>
            <div v-if="ticket.status" class="ticket-drawer__field">
              <dt>Status</dt>
              <dd>
                <span class="ticket-drawer__badge">{{ ticket.status }}</span>
              </dd>
            </div>
            <div v-if="ticket.priority" class="ticket-drawer__field">
              <dt>Priority</dt>
              <dd>{{ ticket.priority }}</dd>
            </div>
            <div v-if="ticket.board" class="ticket-drawer__field">
              <dt>Board</dt>
              <dd>{{ ticket.board }}</dd>
            </div>
            <div v-if="typeDisplay" class="ticket-drawer__field">
              <dt>Type</dt>
              <dd>{{ typeDisplay }}</dd>
            </div>
            <div v-if="closedAtFormatted" class="ticket-drawer__field">
              <dt>Closed</dt>
              <dd>{{ closedAtFormatted }}</dd>
            </div>
          </dl>

          <div v-if="hasTimeData" class="ticket-drawer__section">
            <p class="ticket-drawer__section-label">Time Tracked</p>
            <dl class="ticket-drawer__fields">
              <div v-if="ticket.totalActualHours" class="ticket-drawer__field">
                <dt>Total</dt>
                <dd>{{ formatHours(ticket.totalActualHours) }}</dd>
              </div>
              <div v-if="ticket.billableHours" class="ticket-drawer__field">
                <dt>Billable</dt>
                <dd>{{ formatHours(ticket.billableHours) }}</dd>
              </div>
              <div v-if="ticket.nonBillableHours" class="ticket-drawer__field">
                <dt>Non-billable</dt>
                <dd>{{ formatHours(ticket.nonBillableHours) }}</dd>
              </div>
            </dl>
          </div>

          <div v-if="ticket.slaName" class="ticket-drawer__section">
            <p class="ticket-drawer__section-label">SLA</p>
            <dl class="ticket-drawer__fields">
              <div class="ticket-drawer__field">
                <dt>SLA</dt>
                <dd>{{ ticket.slaName }}</dd>
              </div>
              <div v-if="ticket.slaStatus" class="ticket-drawer__field">
                <dt>Status</dt>
                <dd>{{ ticket.slaStatus }}</dd>
              </div>
              <div v-if="ticket.hasSlaBreach" class="ticket-drawer__field">
                <dt>Breach</dt>
                <dd>
                  <span class="ticket-drawer__badge ticket-drawer__badge--danger">SLA Breached</span>
                </dd>
              </div>
            </dl>
          </div>

          <div v-if="ticket.agreementName" class="ticket-drawer__section">
            <p class="ticket-drawer__section-label">Agreement</p>
            <dl class="ticket-drawer__fields">
              <div class="ticket-drawer__field">
                <dt>Agreement</dt>
                <dd>{{ ticket.agreementName }}</dd>
              </div>
              <div v-if="ticket.agreementType" class="ticket-drawer__field">
                <dt>Type</dt>
                <dd>{{ ticket.agreementType }}</dd>
              </div>
            </dl>
          </div>

          <div class="ticket-drawer__section">
            <p class="ticket-drawer__section-label">Notes</p>
            <p v-if="notesLoading" class="ticket-drawer__notes-loading">Loading…</p>
            <p v-else-if="notesError" class="ticket-drawer__notes-empty">Could not load notes.</p>
            <p v-else-if="!notes.length" class="ticket-drawer__notes-empty">No notes available.</p>
            <div v-else class="ticket-drawer__notes-list">
              <p
                v-for="(note, idx) in notes"
                :key="idx"
                class="ticket-drawer__note"
              >{{ note.text }}</p>
            </div>
          </div>
        </div>

        <div v-if="ticket.connectwiseUrl" class="ticket-drawer__footer">
          <a
            :href="ticket.connectwiseUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="ticket-drawer__cw-link"
          >
            View in ConnectWise
            <b-icon icon="open-in-new" size="is-small" />
          </a>
        </div>
      </aside>
    </div>
  </transition>
</template>

<script>
import axios from 'axios'

export default {
  name: 'TicketDetailDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    ticket: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close'],
  data() {
    return {
      notes: [],
      notesLoading: false,
      notesError: false,
    }
  },
  watch: {
    visible(val) {
      if (val && this.ticket?.ticketNbr) {
        this.fetchNotes(this.ticket.ticketNbr)
      } else if (!val) {
        this.notes = []
        this.notesLoading = false
        this.notesError = false
      }
      this.setBodyScrollLocked(val)
    },
    'ticket.ticketNbr'(val) {
      if (this.visible && val) {
        this.fetchNotes(val)
      }
    },
  },
  beforeUnmount() {
    this.setBodyScrollLocked(false)
  },
  computed: {
    clientName() {
      return this.ticket.companyName || null
    },
    technicianDisplay() {
      return this.ticket.technicianName || this.ticket.closedByName || null
    },
    typeDisplay() {
      if (this.ticket.type && this.ticket.subType) return `${this.ticket.type} / ${this.ticket.subType}`
      return this.ticket.type || this.ticket.subType || null
    },
    closedAtFormatted() {
      if (!this.ticket.closedAt) return null
      try {
        return new Date(this.ticket.closedAt).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      } catch {
        return null
      }
    },
    hasTimeData() {
      return (
        Number(this.ticket.totalActualHours || 0) > 0 ||
        Number(this.ticket.billableHours || 0) > 0 ||
        Number(this.ticket.nonBillableHours || 0) > 0
      )
    },
  },
  methods: {
    setBodyScrollLocked(locked) {
      if (typeof document === 'undefined') return
      // Depending on whether body has an explicit height/overflow set, the browser's
      // actual scrolling context can be <html> rather than <body> -- lock both so this
      // works regardless of which one is the active scroll container.
      document.documentElement.style.overflow = locked ? 'hidden' : ''
      document.body.style.overflow = locked ? 'hidden' : ''
    },
    async fetchNotes(ticketNbr) {
      this.notes = []
      this.notesLoading = true
      this.notesError = false
      try {
        const token = this.$cookies.get('teamwrkr-token')
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/work/tickets/${ticketNbr}/notes`,
          { headers: token ? { Authorization: token } : {} },
        )
        this.notes = Array.isArray(response?.data?.notes) ? response.data.notes : []
      } catch {
        this.notesError = true
      } finally {
        this.notesLoading = false
      }
    },
    formatHours(hours) {
      const total = Number(hours || 0)
      if (!total) return '0m'
      const h = Math.floor(total)
      const m = Math.round((total - h) * 60)
      if (h && m) return `${h}h ${m}m`
      if (h) return `${h}h`
      return `${m}m`
    },
  },
}
</script>

<style lang="scss" scoped>
.ticket-drawer-wrapper {
  position: fixed;
  inset: 0;
  z-index: 1000;
  pointer-events: none;
}

.ticket-drawer-overlay {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.3);
  pointer-events: all;
}

.ticket-drawer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 45vw;
  min-width: 480px;
  max-width: 90vw;
  background: $color-surface;
  border-left: 1px solid $color-border;
  box-shadow: $shadow-lg;
  display: flex;
  flex-direction: column;
  pointer-events: all;
  overflow: hidden;
}

.ticket-drawer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $space-3 $space-4;
  border-bottom: 1px solid $color-border;
  gap: $space-2;
  flex-shrink: 0;
}

.ticket-drawer__number {
  font-size: $font-size-md;
  font-weight: 700;
  color: $color-text-strong;
}

.ticket-drawer__close {
  background: none;
  border: none;
  cursor: pointer;
  padding: $space-1;
  color: $color-text-muted;
  display: flex;
  align-items: center;

  &:hover {
    color: $color-text;
  }
}

.ticket-drawer__body {
  flex: 1;
  overflow-y: auto;
  padding: $space-4;
  display: flex;
  flex-direction: column;
  gap: $space-4;
}

.ticket-drawer__summary {
  margin: 0;
  font-size: $font-size-sm;
  line-height: 1.5;
  color: $color-text;
}

.ticket-drawer__section {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.ticket-drawer__section-label {
  margin: 0;
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $color-text-muted;
}

.ticket-drawer__fields {
  display: flex;
  flex-direction: column;
  gap: $space-2;
  margin: 0;
}

.ticket-drawer__field {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: $space-2;
  align-items: baseline;

  dt {
    font-size: $font-size-xs;
    font-weight: 600;
    color: $color-text-muted;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0;
  }

  dd {
    font-size: $font-size-sm;
    color: $color-text;
    margin: 0;
    word-break: break-word;
  }
}

.ticket-drawer__badge {
  display: inline-block;
  padding: 2px $space-2;
  border-radius: $radius-pill;
  background: $color-surface-muted;
  color: $color-text;
  font-size: $font-size-xs;
  font-weight: 600;
}

.ticket-drawer__badge--danger {
  background: #fee2e2;
  color: #991b1b;
}

.ticket-drawer__notes-list {
  display: flex;
  flex-direction: column;
  gap: $space-3;
}

.ticket-drawer__note {
  margin: 0;
  font-size: $font-size-sm;
  line-height: 1.55;
  color: $color-text;
  white-space: pre-wrap;
  border-left: 2px solid $color-border;
  padding-left: $space-2;
}

.ticket-drawer__notes-loading,
.ticket-drawer__notes-empty {
  margin: 0;
  font-size: $font-size-sm;
  color: $color-text-muted;
}

.ticket-drawer__footer {
  padding: $space-3 $space-4;
  border-top: 1px solid $color-border;
  flex-shrink: 0;
}

.ticket-drawer__cw-link {
  display: inline-flex;
  align-items: center;
  gap: $space-1;
  font-size: $font-size-sm;
  font-weight: 600;
  color: $color-primary-dark;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: opacity 0.2s ease;

  .ticket-drawer {
    transition: transform 0.2s ease;
  }
}

.drawer-slide-enter-from,
.drawer-slide-leave-to {
  opacity: 0;

  .ticket-drawer {
    transform: translateX(100%);
  }
}
</style>
